import React, { Component } from "react";
import PropTypes from "prop-types";
import withStyles from "@material-ui/core/styles/withStyles";

import AccountBalance from "@material-ui/icons/AccountBalance";
import AssignmentIcon from "@material-ui/icons/Assignment";
import GroupWorkIcon from "@material-ui/icons/GroupWork";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import HowToRegIcon from "@material-ui/icons/HowToReg";
import BuildIcon from "@material-ui/icons/Build";
import DateRange from "@material-ui/icons/DateRange";
import ChevronRight from "@material-ui/icons/ChevronRight";
import CelebrationIcon from "@material-ui/icons/EmojiEvents";

import { useAuth } from "context/AuthContext";
import api from "api/index";

// ── Constants ──────────────────────────────────────────────────────────────────

const NAVY = "#1a237e";
const ORANGE = "#e65100"; // the single reserved meaning: overdue / expiring
const NEUTRAL = "#475569";

// Section key -> icon. Icons are identity markers only; colour (not the icon
// itself) is what encodes urgency, per row below.
const SECTION_ICON = {
  arrears: AccountBalance,
  pendingAdmissions: HowToRegIcon,
  pendingClearance: CheckCircleIcon,
  unenrolledStudents: GroupWorkIcon,
  missingMarks: AssignmentIcon
};

const SEVERITY_ORDER = { money: 0, deadline: 1, backlog: 2 };

const styles = {
  pageHeader: { 
    margin: "0 0 32px",
    padding: "24px 0",
    borderBottom: "1px solid #f1f5f9"
  },
  pageTitle: { 
    margin: 0, 
    fontSize: "28px", 
    fontWeight: 800, 
    color: "#0f172a", 
    letterSpacing: "-0.5px" 
  },
  pageSubtitle: { 
    margin: "8px 0 0", 
    fontSize: "15px", 
    color: "#64748b",
    fontWeight: 400
  },

  // ── 1. Context strip ──────────────────────────────────────
  contextStrip: {
    display: "flex", 
    alignItems: "center", 
    flexWrap: "wrap", 
    gap: "12px",
    fontSize: "13px", 
    color: "#475569", 
    fontWeight: 600,
    padding: "12px 16px", 
    marginBottom: "32px",
    background: "#f8fafc",
    borderRadius: "12px",
    border: "1px solid #f1f5f9",
    "& svg": { fontSize: "18px", verticalAlign: "middle", color: NAVY }
  },
  contextDivider: { color: "#cbd5e1", margin: "0 4px" },
  contextStatusPill: {
    display: "inline-flex", 
    alignItems: "center", 
    gap: "6px",
    fontSize: "11px", 
    fontWeight: 800, 
    textTransform: "uppercase", 
    letterSpacing: "0.6px",
    padding: "4px 10px", 
    borderRadius: "20px",
    boxShadow: "0 1px 2px rgba(0,0,0,0.05)"
  },

  // ── 2. Attention queue ────────────────────────────────────────────────────────
  sectionLabel: { 
    margin: "0 0 16px", 
    fontSize: "14px", 
    fontWeight: 800, 
    color: "#475569", 
    textTransform: "uppercase", 
    letterSpacing: "0.8px",
    display: "flex",
    alignItems: "center",
    "&::after": {
      content: '""',
      flex: 1,
      height: "1px",
      background: "#e2e8f0",
      marginLeft: "16px"
    }
  },
  queueWrap: { marginBottom: "40px" },
  queueRow: {
    display: "flex", 
    alignItems: "center", 
    gap: "20px",
    padding: "20px 24px",
    border: "1px solid #e2e8f0",
    borderRadius: "16px",
    marginBottom: "12px",
    cursor: "pointer",
    background: "#fff",
    boxShadow: "0 1px 3px rgba(0,0,0,0.02)",
    transition: "all 0.2s ease-in-out",
    "&:hover": { 
      borderColor: NAVY, 
      boxShadow: "0 10px 20px rgba(15,23,42,0.08)",
      transform: "translateY(-2px)"
    }
  },
  queueRowSetup: { borderStyle: "dashed", background: "#f8fafc" },
  queueIconBox: {
    width: "48px", 
    height: "48px", 
    borderRadius: "12px",
    display: "flex", 
    alignItems: "center", 
    justifyContent: "center", 
    flexShrink: 0,
    boxShadow: "inset 0 2px 4px rgba(0,0,0,0.05)"
  },
  queueIcon: { fontSize: "24px" },
  queueBody: { flex: 1, minWidth: 0 },
  queueHeadline: { margin: 0, fontSize: "16px", fontWeight: 700, color: "#0f172a" },
  queueDetail: { margin: "4px 0 0", fontSize: "13px", color: "#64748b", lineHeight: 1.5 },
  queueAction: {
    display: "flex", 
    alignItems: "center", 
    gap: "4px", 
    flexShrink: 0,
    fontSize: "13px", 
    fontWeight: 700, 
    color: NAVY,
    padding: "8px 12px",
    borderRadius: "8px",
    background: "#f0f2ff",
    transition: "background 0.2s",
    "&:hover": { background: "#e0e4ff" },
    "& svg": { fontSize: "18px" }
  },
  queueEmpty: {
    display: "flex", 
    flexDirection: "column", 
    alignItems: "center", 
    gap: "12px",
    padding: "60px 20px", 
    color: "#94a3b8", 
    textAlign: "center",
    border: "2px dashed #e2e8f0", 
    borderRadius: "20px",
    background: "#f8fafc"
  },
  queueEmptyIcon: { fontSize: "48px", color: "#cbd5e1" },

  // ── 3. Term milestones ────────────────────────────────────────────────────────
  milestonesWrap: { 
    marginBottom: "40px",
    padding: "24px",
    background: "#fff",
    borderRadius: "16px",
    border: "1px solid #e2e8f0",
    boxShadow: "0 1px 3px rgba(0,0,0,0.02)"
  },
  milestoneBarTrack: {
    position: "relative", 
    height: "10px", 
    borderRadius: "5px",
    background: "#f1f5f9", 
    marginTop: "20px", 
    marginBottom: "16px",
    overflow: "hidden"
  },
  milestoneBarFill: { 
    height: "100%", 
    borderRadius: "5px", 
    background: `linear-gradient(90deg, ${NAVY}, #4f46e5)`, 
    transition: "width 1s cubic-bezier(0.4, 0, 0.2, 1)" 
  },
  milestoneToday: {
    position: "absolute", 
    top: "0", 
    width: "4px", 
    height: "100%",
    background: "#fff", 
    opacity: 0.5,
    transform: "translateX(-50%)"
  },
  milestoneLabels: { 
    display: "flex", 
    justifyContent: "space-between", 
    fontSize: "12px", 
    color: "#64748b", 
    fontWeight: 600,
    "& span:nth-child(2)": {
      color: NAVY,
      fontWeight: 800,
      background: "#e0e7ff",
      padding: "2px 8px",
      borderRadius: "4px"
    }
  },

  // ── 4. Metric row ──────────────────────────────────────────────────────────────
  metricRow: { 
    display: "grid", 
    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", 
    gap: "24px", 
    marginBottom: "40px" 
  },
  metricTile: { 
    padding: "24px", 
    border: "1px solid #e2e8f0", 
    borderRadius: "16px", 
    background: "#fff",
    boxShadow: "0 1px 3px rgba(0,0,0,0.02)",
    transition: "all 0.2s",
    position: "relative",
    overflow: "hidden",
    "&:hover": {
      borderColor: NAVY,
      boxShadow: "0 10px 20px rgba(0,0,0,0.05)",
      transform: "translateY(-4px)"
    },
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0, left: 0, width: "4px", height: "100%",
      background: NAVY,
      opacity: 0.8
    }
  },
  metricLabel: { 
    margin: "0 0 12px", 
    fontSize: "12px", 
    fontWeight: 800, 
    color: "#64748b", 
    textTransform: "uppercase", 
    letterSpacing: "1px" 
  },
  metricValue: { 
    margin: 0, 
    fontSize: "32px", 
    fontWeight: 800, 
    color: "#0f172a", 
    fontVariantNumeric: "tabular-nums", 
    letterSpacing: "-1px" 
  },
  metricDenominator: { 
    fontSize: "16px", 
    fontWeight: 600, 
    color: "#94a3b8",
    marginLeft: "4px"
  },
  metricSetup: { 
    fontSize: "14px", 
    color: NAVY, 
    fontWeight: 700, 
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: "4px"
  },

  loadingText: { color: "#94a3b8", fontSize: "14px", padding: "60px 0", textAlign: "center", fontWeight: 500 }
};

// ── Helpers ────────────────────────────────────────────────────────────────────

const fmtMoney = n => {
  if (n == null) return "—";
  if (n >= 1_000_000) return `UGX ${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `UGX ${(n / 1_000).toFixed(0)}K`;
  return `UGX ${n}`;
};

const fmtDate = d => d ? new Date(d).toLocaleDateString("en-UG", { day: "numeric", month: "short", year: "numeric" }) : "—";

function severityTint(status, severity) {
  if (status === "setup") return { bg: "#f1f5f9", fg: "#94a3b8" };
  if (severity === "money" || severity === "deadline") return { bg: "#fff3e0", fg: ORANGE };
  return { bg: "#eef1f6", fg: NEUTRAL };
}

function sortQueue(entries) {
  return entries.slice().sort((a, b) => {
    const [, sectionA] = a;
    const [, sectionB] = b;
    // action items (sorted by severity) come before setup items
    if (sectionA.status !== sectionB.status) return sectionA.status === "action" ? -1 : 1;
    if (sectionA.status === "action") {
      return (SEVERITY_ORDER[sectionA.severity] ?? 9) - (SEVERITY_ORDER[sectionB.severity] ?? 9);
    }
    return 0;
  });
}

// ── Component ──────────────────────────────────────────────────────────────────

class BWillisDashboardInner extends Component {
  state = { context: null, sections: {}, loading: true };

  componentDidMount() {
    this.fetchData();
  }

  fetchData = async () => {
    try {
      const res = await api.get("/dashboard/sections");
      const data = res.data?.data ?? {};
      this.setState({ context: data.context ?? null, sections: data.sections ?? {}, loading: false });
    } catch {
      this.setState({ loading: false });
    }
  };

  render() {
    const { classes, user, history } = this.props;
    const { context, sections, loading } = this.state;

    const firstName = user?.name?.split(" ")[0] ?? "there";

    const queueEntries = Object.entries(sections).filter(([, s]) => s.type === "queue" && s.status !== "clear");
    const sortedQueue = sortQueue(queueEntries);
    const metricEntries = Object.entries(sections).filter(([, s]) => s.type === "metric");

    const semester = context?.semester;
    const inSession = context?.status === "in-session";

    let termPct = null;
    if (semester?.startDate && semester?.endDate) {
      const start = new Date(semester.startDate).getTime();
      const end = new Date(semester.endDate).getTime();
      const now = Date.now();
      termPct = Math.max(0, Math.min(100, Math.round(((now - start) / (end - start)) * 100)));
    }

    return (
      <div>
        <div className={classes.pageHeader}>
          <h3 className={classes.pageTitle}>Dashboard</h3>
          <p className={classes.pageSubtitle}>Welcome back, {firstName}.</p>
        </div>

        {/* 1. Context strip */}
        <div className={classes.contextStrip}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <DateRange />
            {semester ? semester.name : "No active semester"}
            {semester?.academicYear && <span>· {semester.academicYear}</span>}
          </div>
          <span className={classes.contextDivider}>|</span>
          <span
            className={classes.contextStatusPill}
            style={inSession ? { background: "#dcfce7", color: "#166534" } : { background: "#f1f5f9", color: "#475569" }}
          >
            <span style={{ width: "6px", height: "6px", borderRadius: "50%", background: inSession ? "#22c55e" : "#94a3b8" }} />
            {inSession ? "System in session" : "System not in session"}
          </span>
          {context?.daysToClose != null && (
            <>
              <span className={classes.contextDivider}>|</span>
              <span style={{ color: context.daysToClose < 7 ? ORANGE : "#475569", fontWeight: 700 }}>
                {context.daysToClose} day{context.daysToClose === 1 ? "" : "s"} remaining
              </span>
            </>
          )}
        </div>

        {loading ? (
          <p className={classes.loadingText}>Loading your dashboard…</p>
        ) : (
          <>
            {/* 2. Attention queue */}
            <div className={classes.queueWrap}>
              <p className={classes.sectionLabel}>Needs your attention</p>
              {sortedQueue.length === 0 ? (
                <div className={classes.queueEmpty}>
                  <CelebrationIcon className={classes.queueEmptyIcon} />
                  <span>Nothing needs your attention right now.</span>
                </div>
              ) : (
                sortedQueue.map(([key, section]) => {
                  const Icon = section.status === "setup" ? BuildIcon : (SECTION_ICON[key] || AssignmentIcon);
                  const tint = severityTint(section.status, section.severity);
                  return (
                    <div
                      key={key}
                      className={`${classes.queueRow} ${section.status === "setup" ? classes.queueRowSetup : ""}`}
                      onClick={() => section.action?.href && history.push(section.action.href)}
                    >
                      <div className={classes.queueIconBox} style={{ background: tint.bg }}>
                        <Icon className={classes.queueIcon} style={{ color: tint.fg }} />
                      </div>
                      <div className={classes.queueBody}>
                        <p className={classes.queueHeadline}>{section.headline}</p>
                        <p className={classes.queueDetail}>{section.detail}</p>
                      </div>
                      {section.action && (
                        <span className={classes.queueAction}>
                          {section.action.label}
                          <ChevronRight />
                        </span>
                      )}
                    </div>
                  );
                })
              )}
            </div>

            {/* 3. Term milestones */}
            {semester && termPct !== null && (
              <div className={classes.milestonesWrap}>
                <p className={classes.sectionLabel}>Term progress</p>
                <div className={classes.milestoneBarTrack}>
                  <div className={classes.milestoneBarFill} style={{ width: `${termPct}%` }} />
                  <div className={classes.milestoneToday} style={{ left: `${termPct}%` }} title="Today" />
                </div>
                <div className={classes.milestoneLabels}>
                  <span>{fmtDate(semester.startDate)}</span>
                  <span>{termPct}% through {semester.name}</span>
                  <span>{fmtDate(semester.endDate)}</span>
                </div>
              </div>
            )}

            {/* 4. Metric row */}
            {metricEntries.length > 0 && (
              <div className={classes.metricRow}>
                {metricEntries.map(([key, m]) => (
                  <div
                    key={key}
                    className={classes.metricTile}
                    onClick={() => m.href && history.push(m.href)}
                    style={{ cursor: m.href ? "pointer" : "default" }}
                  >
                    <p className={classes.metricLabel}>{m.label}</p>
                    {m.setup ? (
                      <span className={classes.metricSetup}>{m.setupLabel} <ChevronRight style={{ fontSize: "16px" }} /></span>
                    ) : m.format === "money" ? (
                      <div className={classes.metricValue}>
                        {m.denominator > 0 ? Math.round((m.value / m.denominator) * 100) : 0}%
                        <span className={classes.metricDenominator}> collected of {fmtMoney(m.denominator)}</span>
                      </div>
                    ) : (
                      <div className={classes.metricValue}>
                        {m.value}
                        <span className={classes.metricDenominator}> / {m.denominator} {m.label.toLowerCase().includes("student") ? "Students" : "Items"}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    );
  }
}

BWillisDashboardInner.propTypes = {
  classes: PropTypes.object.isRequired,
  user: PropTypes.object,
  history: PropTypes.object.isRequired
};

const BWillisDashboardStyled = withStyles(styles)(BWillisDashboardInner);

function BWillisDashboard(props) {
  const { user } = useAuth();
  return <BWillisDashboardStyled {...props} user={user} />;
}

export default BWillisDashboard;
